import type { NotificationItem } from "../types/notifications";
import { getNotificationEventLabel } from "../constants/notificationEventLabels";
import { formatDisplayCurrency } from "./formatDisplayCurrency";
import { getSyncedDisplayCurrency } from "./displayCurrencySync";
import { formatBrokerNotificationLabel } from "./notificationBrokerDisconnectedDeepLinks";
import { resolveReportTypeDisplayLabel } from "./reportTypeRules";

export const PERFORMANCE_SUMMARY_EVENT_PREFIX = "performance.summary.";

function looksLikeJsonObject(str: string): boolean {
  const t = str.trim();
  return t.startsWith("{") && t.endsWith("}");
}

function parseJsonObject(str: string): Record<string, unknown> | null {
  try {
    const v = JSON.parse(str);
    if (v && typeof v === "object" && !Array.isArray(v)) {
      return v as Record<string, unknown>;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const KEY_LABELS: Record<string, string> = {
  total_trades: "Total trades",
  open_trades: "Active trades",
  closed_trades: "Closed trades",
  winning_trades: "Winning trades",
  losing_trades: "Losing trades",
  scratch_trades: "Scratch trades",
  win_rate: "Win rate",
  total_pnl: "Total P&L",
  net_pnl: "Net P&L",
  gross_pnl: "Gross P&L",
  realized_pnl: "Realized P&L",
  period: "Period",
  period_label: "Period",
  period_start: "Period start",
  period_end: "Period end",
  symbol: "Symbol",
  /** List of per-symbol stats in performance summaries */
  by_symbol: "Symbol breakdown",
  symbol_breakdown: "Symbol breakdown",
  symbols: "Symbol breakdown",
  account_code: "Account",
  broker_account_code: "Broker account",
  long_trades: "Long trades",
  short_trades: "Short trades",
};

function humanizeKey(key: string): string {
  if (KEY_LABELS[key]) return KEY_LABELS[key];
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function isMoneyKey(key: string): boolean {
  return /pnl|profit|loss|amount|fees|revenue|balance|equity|value/i.test(key);
}

function formatScalar(key: string, value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    const k = key.toLowerCase();
    if (k.includes("win_rate") && value > 0 && value <= 1) {
      return `${(value * 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
    }
    if (k.includes("rate") || k.includes("percent")) {
      return `${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;
    }
    if (isMoneyKey(key)) {
      return formatDisplayCurrency(value, getSyncedDisplayCurrency(), { locale: "en-US" });
    }
    return value.toLocaleString("en-US", { maximumFractionDigits: 4 });
  }
  if (typeof value === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(value) && !Number.isNaN(Date.parse(value))) {
      try {
        return new Date(value).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        /* fall through */
      }
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((x) => formatScalar(key, x)).join(", ");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
}

const KEY_ORDER: string[] = [
  "period_label",
  "period",
  "period_start",
  "period_end",
  "account_code",
  "broker_account_code",
  "total_trades",
  "open_trades",
  "closed_trades",
  "winning_trades",
  "losing_trades",
  "scratch_trades",
  "long_trades",
  "short_trades",
  "win_rate",
  "total_pnl",
  "net_pnl",
  "gross_pnl",
  "realized_pnl",
];

function sortedKeys(obj: Record<string, unknown>): string[] {
  const keys = Object.keys(obj);
  const ordered = KEY_ORDER.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !KEY_ORDER.includes(k)).sort();
  return [...ordered, ...rest];
}

function formatPayloadAsHtml(obj: Record<string, unknown>): string {
  const rows = sortedKeys(obj).map((key) => {
    const raw = obj[key];
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      const inner = formatPayloadAsHtml(raw as Record<string, unknown>);
      return `<li><strong>${escapeHtml(humanizeKey(key))}</strong>${inner}</li>`;
    }
    const display = escapeHtml(formatScalar(key, raw));
    return `<li><strong>${escapeHtml(humanizeKey(key))}:</strong> ${display}</li>`;
  });
  return `<ul style="margin:0;padding-left:1.1em;list-style:disc">${rows.join("")}</ul>`;
}

export interface PerformanceSummaryMetricEntry {
  id: string;
  label: string;
  value: string;
}

/** Normalize API keys (snake_case or camelCase) for omission checks. */
function canonicalMetricKey(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1_$2")
    .toLowerCase();
}

/**
 * Keys hidden on performance summary metric cards: window bounds, local/UTC variants,
 * period metadata, timeframe, and timezone (redundant or not useful in this UI).
 */
function isOmittedPerformanceSummaryKey(key: string): boolean {
  const c = canonicalMetricKey(key);
  if (c.startsWith("period_start")) return true;
  if (c.startsWith("period_end")) return true;
  if (c === "period_type" || c === "periodtype") return true;
  if (c === "timeframe") return true;
  if (c === "timezone" || c === "timezone_source" || c === "timezonesource") return true;
  if (c.startsWith("timezone_")) return true;
  if (c === "reporting_timezone" || c === "display_timezone") return true;
  return false;
}

function sortedKeysForPerformanceSummary(
  o: Record<string, unknown>,
  prefix: string
): string[] {
  let keys = sortedKeys(o).filter((k) => !isOmittedPerformanceSummaryKey(k));
  const periodRaw = o["period"];
  const hasDayPeriod = typeof periodRaw === "string" && periodRaw.trim().length > 0;
  if (prefix === "" && hasDayPeriod) {
    keys = keys.filter((k) => k !== "period_label");
  }
  return keys;
}

function pickSymbolFromBreakdownRow(row: Record<string, unknown>): string | null {
  const s = row.symbol ?? row.ticker ?? row.instrument;
  if (typeof s === "string" && s.trim()) return s.trim();
  return null;
}

function isSymbolBreakdownRow(row: unknown): row is Record<string, unknown> {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  return pickSymbolFromBreakdownRow(row as Record<string, unknown>) != null;
}

/** Flatten notification `data` into label/value rows for performance summary UIs. */
export function buildPerformanceSummaryEntries(
  data: NotificationItem["data"]
): PerformanceSummaryMetricEntry[] {
  const obj = coerceDataRecord(data);
  if (!obj) return [];
  const out: PerformanceSummaryMetricEntry[] = [];
  let seq = 0;
  const walk = (o: Record<string, unknown>, prefix: string) => {
    for (const key of sortedKeysForPerformanceSummary(o, prefix)) {
      const raw = o[key];
      const segment = humanizeKey(key);
      const label = prefix ? `${prefix} · ${segment}` : segment;
      if (Array.isArray(raw)) {
        if (raw.length === 0) {
          continue;
        }
        if (raw.every(isSymbolBreakdownRow)) {
          const names = (raw as Record<string, unknown>[])
            .map((r) => pickSymbolFromBreakdownRow(r))
            .filter((s): s is string => Boolean(s && s.length));
          if (names.length === 0) {
            continue;
          }
          out.push({
            id: `m-${seq++}`,
            label,
            value: names.join(", "),
          });
          continue;
        }
        out.push({ id: `m-${seq++}`, label, value: formatScalar(key, raw) });
        continue;
      }
      if (raw !== null && typeof raw === "object") {
        walk(raw as Record<string, unknown>, label);
      } else {
        out.push({ id: `m-${seq++}`, label, value: formatScalar(key, raw) });
      }
    }
  };
  walk(obj, "");
  return out;
}

export function isPerformanceSummaryEventKey(eventKey: string | null | undefined): boolean {
  if (!eventKey) return false;
  return (
    eventKey === "performance.summary.daily" ||
    eventKey === "performance.summary.weekly" ||
    eventKey === "performance.summary.monthly" ||
    eventKey.startsWith(PERFORMANCE_SUMMARY_EVENT_PREFIX)
  );
}

export function isNewBestTradingDayEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "performance.daily.new_best_day";
}

export function isNewWorstTradingDayEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "performance.daily.new_worst_day";
}

export function isRecordTradingDayEventKey(eventKey: string | null | undefined): boolean {
  return isNewBestTradingDayEventKey(eventKey) || isNewWorstTradingDayEventKey(eventKey);
}

export function isPnlMilestoneCrossedEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "performance.pnl.milestone_crossed";
}

export function isBrokerDisconnectedEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "system.broker_disconnected";
}

export function isBrokerReconnectedEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "system.broker_reconnected";
}

/** Broker disconnected / reconnected — message-only with broker-management CTA. */
export function isBrokerConnectionEventKey(eventKey: string | null | undefined): boolean {
  return isBrokerDisconnectedEventKey(eventKey) || isBrokerReconnectedEventKey(eventKey);
}

export function isSyncFailedEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "system.sync_failed";
}

export function isSyncRecoveredEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "system.sync_recovered";
}

export function isBrokerSyncEventKey(eventKey: string | null | undefined): boolean {
  return isSyncFailedEventKey(eventKey) || isSyncRecoveredEventKey(eventKey);
}

/** System broker/sync notifications that use message + under-message CTA layout. */
export function isSystemBrokerDetailEventKey(eventKey: string | null | undefined): boolean {
  return isBrokerConnectionEventKey(eventKey) || isBrokerSyncEventKey(eventKey);
}

export function isBillingPaymentSucceededEventKey(
  eventKey: string | null | undefined
): boolean {
  return eventKey === "billing.payment_succeeded";
}

export function isBillingPaymentFailedEventKey(
  eventKey: string | null | undefined
): boolean {
  return eventKey === "billing.payment_failed";
}

export function isBillingPaymentActionRequiredEventKey(
  eventKey: string | null | undefined
): boolean {
  return (
    eventKey === "billing.invoice.payment_action_required" ||
    eventKey === "billing.payment_action_required"
  );
}

export function isBillingSubscriptionCancelledEventKey(
  eventKey: string | null | undefined
): boolean {
  return eventKey === "billing.subscription.cancelled";
}

export function isBillingSubscriptionUpdatedEventKey(
  eventKey: string | null | undefined
): boolean {
  return eventKey === "billing.subscription.updated";
}

/** Active billing notifications (trials excluded) — message + billing CTA layout. */
export function isActiveBillingEventKey(eventKey: string | null | undefined): boolean {
  return (
    isBillingPaymentSucceededEventKey(eventKey) ||
    isBillingPaymentFailedEventKey(eventKey) ||
    isBillingPaymentActionRequiredEventKey(eventKey) ||
    isBillingSubscriptionCancelledEventKey(eventKey) ||
    isBillingSubscriptionUpdatedEventKey(eventKey)
  );
}

export function isReportReadyEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "report_ready";
}

export function isReportFailedEventKey(eventKey: string | null | undefined): boolean {
  return eventKey === "report_failed";
}

/** Report ready / failed — message + under-message CTA layout. */
export function isReportDetailEventKey(eventKey: string | null | undefined): boolean {
  return isReportReadyEventKey(eventKey) || isReportFailedEventKey(eventKey);
}

function pickDataNumber(obj: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string" && v.trim() && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

function pickDataString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function formatSignedPercent(value: number): string {
  const normalized = value > 0 && value <= 1 ? value * 100 : value;
  const abs = Math.abs(normalized).toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (normalized > 0) return `+${abs}%`;
  if (normalized < 0) return `-${abs}%`;
  return `${abs}%`;
}

function formatCalendarDayPhrase(raw: string): string | null {
  const ymd = ymdFromIsoOrDate(raw);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

const RECORD_DAY_ACCOUNT_KEYS = [
  "broker_account_code",
  "brokerAccountCode",
  "account_code",
  "accountCode",
] as const;

function pickRecordDayAccount(obj: Record<string, unknown>): string | undefined {
  return pickDataString(obj, [...RECORD_DAY_ACCOUNT_KEYS]);
}

type RecordTradingDayKind = "best" | "worst";

/**
 * Supportive in-app copy for new-best / new-worst day notifications.
 * Uses P&L, P&L %, previous record, and previous date (not account / milestone metadata).
 */
function formatRecordTradingDayMessageHtml(
  data: NotificationItem["data"],
  kind: RecordTradingDayKind
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const pnl = pickDataNumber(obj, ["pnl_abs", "pnlAbs", "realized_pnl", "realizedPnl"]);
  const pct = pickDataNumber(obj, ["pnl_pct", "pnlPct"]);
  const previous = pickDataNumber(obj, [
    "previous_record_pnl_abs",
    "previousRecordPnlAbs",
  ]);
  const previousDateRaw = pickDataString(obj, [
    "previous_record_date",
    "previousRecordDate",
  ]);
  const periodRaw =
    pickDataString(obj, ["period_label", "periodLabel", "period"]) ??
    pickDataString(obj, ["period_start_local", "periodStartLocal", "period_start", "periodStart"]);

  if (pnl === undefined && previous === undefined) return null;

  const currencyRaw = pickDataString(obj, ["display_currency", "displayCurrency"]);
  const currency =
    currencyRaw && /^[A-Za-z]{3}$/.test(currencyRaw)
      ? currencyRaw.toUpperCase()
      : getSyncedDisplayCurrency();

  /** Tropical Surf — dates */
  const dateSpan = (phrase: string) =>
    `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(phrase)}</strong>`;
  /** Glacial green (best) / neon red (worst) — P&L figures */
  const pnlColor = kind === "best" ? "#06E363" : "#F70009";
  const pnlSpan = (text: string) =>
    `<strong style="color:${pnlColor};">${text}</strong>`;

  const money = (n: number) =>
    pnlSpan(
      escapeHtml(formatDisplayCurrency(n, currency, { locale: "en-US", showSign: true }))
    );

  const dayPhrase = periodRaw ? formatCalendarDayPhrase(periodRaw) : null;
  const previousDayPhrase = previousDateRaw
    ? formatCalendarDayPhrase(previousDateRaw)
    : null;

  const opener =
    kind === "best"
      ? dayPhrase
        ? `Nice work. ${dateSpan(dayPhrase)} is your new best trading day.`
        : "Nice work. You just set a new best trading day."
      : dayPhrase
        ? `${dateSpan(dayPhrase)} is your new worst trading day. Big losing days are normal. They're part of the game.`
        : "This is your new worst trading day. Big losing days are normal. They're part of the game.";

  const parts: string[] = [];
  if (pnl !== undefined) {
    parts.push(`You closed the session at ${money(pnl)}`);
    if (pct !== undefined) {
      parts.push(` (${pnlSpan(escapeHtml(formatSignedPercent(pct)))})`);
    }
  }

  if (previous !== undefined) {
    const compareVerb = kind === "best" ? "beating" : "below";
    const recordLabel = kind === "best" ? "previous best" : "previous worst";
    const prevClause = previousDayPhrase
      ? `${compareVerb} your ${recordLabel} of ${money(previous)} from ${dateSpan(previousDayPhrase)}`
      : `${compareVerb} your ${recordLabel} of ${money(previous)}`;
    if (parts.length > 0) {
      parts.push(`, ${prevClause}`);
    } else {
      parts.push(prevClause.charAt(0).toUpperCase() + prevClause.slice(1));
    }
  }

  const detail = parts.length > 0 ? `${parts.join("")}.` : "";
  const dive =
    kind === "worst"
      ? "Dive into this day in Statistics or Trade Review, or generate a report to review the details."
      : "Dive into this day in Statistics, or generate a report to review the details.";
  if (detail) {
    return `<p style="margin:0 0 0.75em;">${opener}</p><p style="margin:0 0 0.75em;">${detail}</p><p style="margin:0;">${dive}</p>`;
  }
  return `<p style="margin:0 0 0.75em;">${opener}</p><p style="margin:0;">${dive}</p>`;
}

export function formatNewBestTradingDayMessageHtml(
  data: NotificationItem["data"]
): string | null {
  return formatRecordTradingDayMessageHtml(data, "best");
}

export function formatNewWorstTradingDayMessageHtml(
  data: NotificationItem["data"]
): string | null {
  return formatRecordTradingDayMessageHtml(data, "worst");
}

function formatPlainPercentThreshold(value: number): string {
  const normalized = value > 0 && value <= 1 ? value * 100 : value;
  const abs = Math.abs(normalized).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });
  return `${abs}%`;
}

/**
 * Supportive in-app copy for PnL milestone crossed notifications.
 * Uses threshold, crossed day, generated P&L / return / delta (not raw metadata keys).
 */
export function formatPnlMilestoneCrossedMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const milestoneValue = pickDataNumber(obj, ["milestone_value", "milestoneValue"]);
  const milestoneType = (
    pickDataString(obj, ["milestone_type", "milestoneType"]) ?? ""
  ).toLowerCase();
  const crossedRaw = pickDataString(obj, ["crossed_at", "crossedAt"]);
  const currentPnl = pickDataNumber(obj, [
    "current_generated_pnl",
    "currentGeneratedPnl",
  ]);
  const deltaPnl = pickDataNumber(obj, ["delta_generated_pnl", "deltaGeneratedPnl"]);
  const returnPct = pickDataNumber(obj, ["current_return_pct", "currentReturnPct"]);

  if (
    milestoneValue === undefined &&
    currentPnl === undefined &&
    deltaPnl === undefined
  ) {
    return null;
  }

  const currencyRaw = pickDataString(obj, ["display_currency", "displayCurrency"]);
  const currency =
    currencyRaw && /^[A-Za-z]{3}$/.test(currencyRaw)
      ? currencyRaw.toUpperCase()
      : getSyncedDisplayCurrency();

  const dateSpan = (phrase: string) =>
    `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(phrase)}</strong>`;
  const pnlSpan = (text: string) =>
    `<strong style="color:#06E363;">${text}</strong>`;
  const money = (n: number) =>
    pnlSpan(
      escapeHtml(formatDisplayCurrency(n, currency, { locale: "en-US", showSign: true }))
    );

  const isPercent =
    milestoneType === "percent" || milestoneType.includes("percent");
  const thresholdLabel =
    milestoneValue === undefined
      ? null
      : isPercent
        ? pnlSpan(escapeHtml(formatPlainPercentThreshold(milestoneValue)))
        : money(milestoneValue);

  const dayPhrase = crossedRaw ? formatCalendarDayPhrase(crossedRaw) : null;

  const opener = thresholdLabel
    ? dayPhrase
      ? `You crossed your ${thresholdLabel} milestone on ${dateSpan(dayPhrase)}.`
      : `You crossed your ${thresholdLabel} milestone.`
    : dayPhrase
      ? `You crossed a PnL milestone on ${dateSpan(dayPhrase)}.`
      : "You crossed a PnL milestone.";

  const detailParts: string[] = [];
  if (currentPnl !== undefined) {
    detailParts.push(`Generated P&amp;L is now ${money(currentPnl)}`);
    if (returnPct !== undefined) {
      detailParts.push(` (${pnlSpan(escapeHtml(formatSignedPercent(returnPct)))})`);
    }
  } else if (returnPct !== undefined) {
    detailParts.push(
      `Return is now ${pnlSpan(escapeHtml(formatSignedPercent(returnPct)))}`
    );
  }
  if (deltaPnl !== undefined) {
    const deltaClause = `up ${money(deltaPnl)} from your baseline`;
    if (detailParts.length > 0) {
      detailParts.push(`, ${deltaClause}`);
    } else {
      detailParts.push(deltaClause.charAt(0).toUpperCase() + deltaClause.slice(1));
    }
  }

  const detail = detailParts.length > 0 ? `${detailParts.join("")}.` : "";
  if (detail) {
    return `<p style="margin:0 0 0.75em;">${opener}</p><p style="margin:0;">${detail}</p>`;
  }
  return `<p style="margin:0;">${opener}</p>`;
}

/**
 * In-app copy for broker disconnected — which broker dropped; reconnect CTA is separate.
 */
export function formatBrokerDisconnectedMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const brokerRaw = pickDataString(obj, [
    "broker_display_name",
    "brokerDisplayName",
    "broker_name",
    "brokerName",
    "broker",
  ]);
  if (!brokerRaw) return null;

  const brokerLabel = formatBrokerNotificationLabel(brokerRaw);
  const brokerStrong = `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(brokerLabel)}</strong>`;

  return `<p style="margin:0;">Your ${brokerStrong} connection was lost. Syncing is paused until it reconnects.</p>`;
}

/**
 * In-app copy for broker reconnected — which broker came back; view-connection CTA is separate.
 */
export function formatBrokerReconnectedMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const brokerRaw = pickDataString(obj, [
    "broker_display_name",
    "brokerDisplayName",
    "broker_name",
    "brokerName",
    "broker",
  ]);
  if (!brokerRaw) return null;

  const brokerLabel = formatBrokerNotificationLabel(brokerRaw);
  const brokerStrong = `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(brokerLabel)}</strong>`;

  return `<p style="margin:0;">Your ${brokerStrong} connection is back online. Syncing has resumed.</p>`;
}

/**
 * In-app copy for sync failed — account + optional last error; check-sync CTA is separate.
 */
export function formatSyncFailedMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const account = pickRecordDayAccount(obj);
  const error = pickDataString(obj, ["error", "last_error", "lastError", "message"]);

  const accountSpan = (phrase: string) =>
    `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(phrase)}</strong>`;
  const errorSpan = (phrase: string) =>
    `<strong style="color:var(--brand-glacial-red);">${escapeHtml(phrase)}</strong>`;

  const accountClause = account ? ` for ${accountSpan(account)}` : "";
  const errorClause = error ? ` Last error: ${errorSpan(error)}.` : "";

  return `<p style="margin:0;">We couldn't sync trades${accountClause}.${errorClause} We'll retry automatically.</p>`;
}

/**
 * In-app copy for sync recovered — account when known; view-connection CTA is separate.
 */
export function formatSyncRecoveredMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const account = pickRecordDayAccount(obj);
  const accountStrong = account
    ? `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(account)}</strong>`
    : null;

  if (accountStrong) {
    return `<p style="margin:0;">Trade sync for ${accountStrong} is healthy again. Your latest fills should appear shortly.</p>`;
  }
  return `<p style="margin:0;">Trade sync is healthy again. Your latest fills should appear shortly.</p>`;
}

function formatBillingAmountPhrase(obj: Record<string, unknown>): string | null {
  const display = pickDataString(obj, [
    "amount_paid_display",
    "amountPaidDisplay",
    "amount_display",
    "amountDisplay",
  ]);
  const currencyRaw = pickDataString(obj, ["currency", "display_currency", "displayCurrency"]);
  const currency =
    currencyRaw && /^[A-Za-z]{3}$/.test(currencyRaw)
      ? currencyRaw.toUpperCase()
      : null;

  if (display) {
    const cleaned = display.trim();
    if (currency && !new RegExp(currency, "i").test(cleaned) && !/\$|€|£/.test(cleaned)) {
      return `${cleaned} ${currency}`;
    }
    return cleaned;
  }

  const cents = pickDataNumber(obj, ["amount_paid", "amountPaid", "amount"]);
  if (cents === undefined) return null;
  const major = Math.abs(cents) >= 1000 || Number.isInteger(cents) ? cents / 100 : cents;
  const ccy = currency || getSyncedDisplayCurrency();
  return formatDisplayCurrency(major, ccy, { locale: "en-US", showSign: false });
}

export function formatBillingPaymentSucceededMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;
  const amount = formatBillingAmountPhrase(obj);
  const amountStrong = amount
    ? `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(amount)}</strong>`
    : null;
  if (amountStrong) {
    return `<p style="margin:0;">Thanks. We received ${amountStrong}. Your access continues as normal.</p>`;
  }
  return `<p style="margin:0;">Thanks. We received your payment. Your access continues as normal.</p>`;
}

export function formatBillingPaymentFailedMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;
  const attempt = pickDataNumber(obj, ["attempt_count", "attemptCount"]);
  const attemptClause =
    attempt !== undefined
      ? ` (attempt <strong style="color:var(--brand-glacial-red);">${escapeHtml(String(attempt))}</strong>)`
      : "";
  return `<p style="margin:0;">We couldn't process your subscription payment${attemptClause}. Please update your card.</p>`;
}

export function formatBillingPaymentActionRequiredMessageHtml(
  _data: NotificationItem["data"]
): string | null {
  return `<p style="margin:0;">Your bank needs a quick authentication step (SCA / 3DS) to complete payment. This isn't a failed charge.</p>`;
}

export function formatReportReadyMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const typeKey = pickDataString(obj, ["report_type", "reportType"]);
  const typeLabelRaw = pickDataString(obj, [
    "report_type_label",
    "reportTypeLabel",
    "report_type_display",
    "reportTypeDisplay",
  ]);
  const typeLabel =
    typeKey || typeLabelRaw
      ? resolveReportTypeDisplayLabel(typeKey, typeLabelRaw)
      : undefined;

  const name = pickDataString(obj, [
    "report_name",
    "reportName",
    "title",
    "report_title",
    "reportTitle",
  ]);

  const typeStrong = typeLabel
    ? `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(typeLabel)}</strong>`
    : null;
  const nameStrong = name
    ? `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(name)}</strong>`
    : null;

  if (typeStrong && nameStrong) {
    return `<p style="margin:0;">Your ${typeStrong} report (${nameStrong}) is ready to view.</p>`;
  }
  if (typeStrong) {
    return `<p style="margin:0;">Your ${typeStrong} report is ready to view.</p>`;
  }
  if (nameStrong) {
    return `<p style="margin:0;">Your report (${nameStrong}) is ready to view.</p>`;
  }
  return `<p style="margin:0;">Your report is ready to view.</p>`;
}

function pickReportTypeAndName(obj: Record<string, unknown>): {
  typeLabel?: string;
  name?: string;
} {
  const typeKey = pickDataString(obj, ["report_type", "reportType"]);
  const typeLabelRaw = pickDataString(obj, [
    "report_type_label",
    "reportTypeLabel",
    "report_type_display",
    "reportTypeDisplay",
  ]);
  const typeLabel =
    typeKey || typeLabelRaw
      ? resolveReportTypeDisplayLabel(typeKey, typeLabelRaw)
      : undefined;
  const name = pickDataString(obj, [
    "report_name",
    "reportName",
    "title",
    "report_title",
    "reportTitle",
  ]);
  return { typeLabel, name };
}

export function formatReportFailedMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const { typeLabel, name } = pickReportTypeAndName(obj);
  const typeStrong = typeLabel
    ? `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(typeLabel)}</strong>`
    : null;
  const nameStrong = name
    ? `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(name)}</strong>`
    : null;

  let lead: string;
  if (typeStrong && nameStrong) {
    lead = `We couldn't generate your ${typeStrong} report (${nameStrong}).`;
  } else if (typeStrong) {
    lead = `We couldn't generate your ${typeStrong} report.`;
  } else if (nameStrong) {
    lead = `We couldn't generate your report (${nameStrong}).`;
  } else {
    lead = `We couldn't generate your report.`;
  }

  const error = pickDataString(obj, [
    "message",
    "user_message",
    "userMessage",
    "error_message",
    "errorMessage",
    "error",
  ]);
  // Prefer human copy; skip raw slug-looking errors like reports.job.stale_reaped
  const errorLine =
    error && !/^[a-z0-9]+(\.[a-z0-9_-]+)+$/i.test(error)
      ? `<p style="margin:0.5rem 0 0;"><strong style="color:var(--brand-glacial-red);">${escapeHtml(error)}</strong></p>`
      : "";

  const actionHint = pickDataString(obj, ["action", "user_action", "userAction"]);
  const actionLine =
    actionHint && actionHint !== error
      ? `<p style="margin:0.5rem 0 0;">${escapeHtml(actionHint)}</p>`
      : "";

  return `<p style="margin:0;">${lead}</p>${errorLine}${actionLine}`;
}

export function formatBillingSubscriptionCancelledMessageHtml(
  _data: NotificationItem["data"]
): string | null {
  return `<p style="margin:0;">Your Tessera subscription has been cancelled. You can re-activate anytime.</p>`;
}

export function formatBillingSubscriptionUpdatedMessageHtml(
  data: NotificationItem["data"]
): string | null {
  const obj = coerceDataRecord(data);
  if (!obj) {
    return `<p style="margin:0;">Your subscription status was updated. Review billing details if anything looks off.</p>`;
  }
  const status = pickDataString(obj, ["status", "subscription_status", "subscriptionStatus"]);
  if (status) {
    const statusStrong = `<strong style="color:var(--brand-tropical-surf);">${escapeHtml(status)}</strong>`;
    return `<p style="margin:0;">Your subscription status was updated to ${statusStrong}. Review billing details if anything looks off.</p>`;
  }
  return `<p style="margin:0;">Your subscription status was updated. Review billing details if anything looks off.</p>`;
}

function formatIsoDateForSummaryPhrase(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const t = raw.trim();
  const ms = Date.parse(t);
  if (Number.isNaN(ms)) return t;
  return new Date(ms).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Day phrase for list previews: `period` only (the summary day). Falls back to `period_label`
 * if `period` is missing. Never uses `period_start` / `period_end`.
 */
function getPerformanceSummaryPeriodPhrase(obj: Record<string, unknown>): string | null {
  const period = obj["period"];
  if (typeof period === "string" && period.trim()) {
    const asDate = formatIsoDateForSummaryPhrase(period);
    return asDate ?? period.trim();
  }
  const label = obj["period_label"];
  if (typeof label === "string" && label.trim()) return label.trim();
  return null;
}

function ymdFromIsoOrDate(raw: string): string | null {
  const t = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const ms = Date.parse(t);
  if (Number.isNaN(ms)) return null;
  return t.slice(0, 10);
}

/** Exclusive period end → inclusive calendar day (YYYY-MM-DD). */
function exclusiveEndToInclusiveYmd(exclusiveEnd: string): string | null {
  const ymd = ymdFromIsoOrDate(exclusiveEnd);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}

function pickPeriodString(obj: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}

function parsePeriodLabelYmdRange(
  label: string
): { start: string; end: string } | { single: string } | null {
  const t = label.trim();
  const parts = t.split(/\s*[—–]\s*|\s+-\s+/);
  if (parts.length === 2) {
    const start = ymdFromIsoOrDate(parts[0]!);
    const end = ymdFromIsoOrDate(parts[1]!);
    if (start && end) return { start, end };
  }
  const single = ymdFromIsoOrDate(t);
  if (single) return { single };
  return null;
}

function formatTitleCalendarDay(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatTitleCalendarMonth(ymd: string): string {
  const [y, m] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
}

function formatTitleShortDateRange(startYmd: string, endYmd: string): string {
  const [ys, ms, ds] = startYmd.split("-").map(Number);
  const [ye, me, de] = endYmd.split("-").map(Number);
  const start = new Date(Date.UTC(ys, ms - 1, ds));
  const end = new Date(Date.UTC(ye, me - 1, de));
  if (ys === ye) {
    const a = start.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const b = end.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${a} – ${b}`;
  }
  const a = start.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const b = end.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${a} – ${b}`;
}

function performanceSummaryCadence(
  eventKey: string | null | undefined
): "daily" | "weekly" | "monthly" | null {
  if (!eventKey) return null;
  if (eventKey === "performance.summary.weekly" || eventKey.endsWith(".weekly")) {
    return "weekly";
  }
  if (eventKey === "performance.summary.monthly" || eventKey.endsWith(".monthly")) {
    return "monthly";
  }
  if (eventKey === "performance.summary.daily" || eventKey.endsWith(".daily")) {
    return "daily";
  }
  return null;
}

/**
 * Human-readable window for list/detail titles (daily / weekly / monthly summaries).
 */
export function formatPerformanceSummaryTitleRange(
  eventKey: string | null | undefined,
  data: NotificationItem["data"]
): string | null {
  const cadence = performanceSummaryCadence(eventKey);
  if (!cadence) return null;
  const obj = coerceDataRecord(data);
  if (!obj) return null;

  const periodLabel = pickPeriodString(obj, ["period_label", "periodLabel"]);
  if (periodLabel) {
    const parsed = parsePeriodLabelYmdRange(periodLabel);
    if (parsed) {
      if ("single" in parsed) {
        return cadence === "monthly"
          ? formatTitleCalendarMonth(parsed.single)
          : formatTitleCalendarDay(parsed.single);
      }
      if (cadence === "monthly") {
        return formatTitleCalendarMonth(parsed.start);
      }
      if (cadence === "daily" && parsed.start === parsed.end) {
        return formatTitleCalendarDay(parsed.start);
      }
      return formatTitleShortDateRange(parsed.start, parsed.end);
    }
    return periodLabel;
  }

  const period = pickPeriodString(obj, ["period"]);
  if (period) {
    const ymd = ymdFromIsoOrDate(period);
    if (ymd) {
      return cadence === "monthly" ? formatTitleCalendarMonth(ymd) : formatTitleCalendarDay(ymd);
    }
  }

  const startRaw = pickPeriodString(obj, [
    "period_start_local",
    "periodStartLocal",
    "period_start",
    "periodStart",
    "local_start_date",
    "localStartDate",
  ]);
  const endRaw = pickPeriodString(obj, [
    "period_end_local",
    "periodEndLocal",
    "period_end",
    "periodEnd",
    "local_end_date",
    "localEndDate",
  ]);
  if (startRaw && endRaw) {
    const startYmd = ymdFromIsoOrDate(startRaw);
    const endLooksExclusive = Boolean(
      obj.period_end_local ||
        obj.periodEndLocal ||
        obj.period_end ||
        obj.periodEnd
    );
    const endYmd = endLooksExclusive
      ? exclusiveEndToInclusiveYmd(endRaw)
      : ymdFromIsoOrDate(endRaw);
    if (startYmd && endYmd) {
      if (cadence === "monthly") {
        return formatTitleCalendarMonth(startYmd);
      }
      if (cadence === "daily" && startYmd === endYmd) {
        return formatTitleCalendarDay(startYmd);
      }
      return formatTitleShortDateRange(startYmd, endYmd);
    }
  }

  return null;
}

/** In-app title for performance summary notifications (account + reporting window when known). */
export function getPerformanceSummaryDisplayTitle(
  eventKey: string | null | undefined,
  data: NotificationItem["data"],
  fallbackTitle: string
): string {
  const canonical = getNotificationEventLabel(eventKey) ?? fallbackTitle;
  if (!isPerformanceSummaryEventKey(eventKey)) return canonical;

  const obj = coerceDataRecord(data);
  const account = obj
    ? pickPeriodString(obj, [
        "broker_account_code",
        "brokerAccountCode",
        "account_code",
        "accountCode",
      ])
    : undefined;
  const range = formatPerformanceSummaryTitleRange(eventKey, data);

  const parts = [canonical];
  if (account && !canonical.includes(account)) {
    parts.push(account);
  }
  if (range && !parts.some((p) => p.includes(range))) {
    parts.push(range);
  }
  return parts.join(" · ");
}

/** In-app title for new-best / new-worst day notifications (includes account when known). */
export function getRecordTradingDayDisplayTitle(
  eventKey: string | null | undefined,
  data: NotificationItem["data"],
  fallbackTitle: string
): string {
  const canonical = getNotificationEventLabel(eventKey) ?? fallbackTitle;
  if (!isRecordTradingDayEventKey(eventKey)) return canonical;

  const obj = coerceDataRecord(data);
  const account = obj ? pickRecordDayAccount(obj) : undefined;

  if (!account || canonical.includes(account)) return canonical;
  return `${canonical} on '${account}'`;
}

/** In-app title for PnL milestone crossed (includes account when known). */
export function getPnlMilestoneCrossedDisplayTitle(
  eventKey: string | null | undefined,
  data: NotificationItem["data"],
  fallbackTitle: string
): string {
  const canonical = getNotificationEventLabel(eventKey) ?? fallbackTitle;
  if (!isPnlMilestoneCrossedEventKey(eventKey)) return canonical;

  const obj = coerceDataRecord(data);
  const account = obj ? pickRecordDayAccount(obj) : undefined;

  if (!account || canonical.includes(account)) return canonical;
  return `${canonical} on '${account}'`;
}

/** In-app title for broker/sync system notifications (includes account when known). */
export function getBrokerConnectionDisplayTitle(
  eventKey: string | null | undefined,
  data: NotificationItem["data"],
  fallbackTitle: string
): string {
  const canonical = getNotificationEventLabel(eventKey) ?? fallbackTitle;
  if (!isSystemBrokerDetailEventKey(eventKey)) return canonical;

  const obj = coerceDataRecord(data);
  const account = obj ? pickRecordDayAccount(obj) : undefined;

  if (!account || canonical.includes(account)) return canonical;
  return `${canonical} on '${account}'`;
}

/** @deprecated Prefer getBrokerConnectionDisplayTitle */
export function getBrokerDisconnectedDisplayTitle(
  eventKey: string | null | undefined,
  data: NotificationItem["data"],
  fallbackTitle: string
): string {
  return getBrokerConnectionDisplayTitle(eventKey, data, fallbackTitle);
}

/** @deprecated Prefer getRecordTradingDayDisplayTitle */
export function getNewBestTradingDayDisplayTitle(
  eventKey: string | null | undefined,
  data: NotificationItem["data"],
  fallbackTitle: string
): string {
  return getRecordTradingDayDisplayTitle(eventKey, data, fallbackTitle);
}

export function resolveNotificationDisplayTitle(
  title: string,
  eventKey: string | null | undefined,
  data: NotificationItem["data"]
): string {
  const canonical = getNotificationEventLabel(eventKey);
  const base = canonical ?? title;
  if (isPerformanceSummaryEventKey(eventKey)) {
    return getPerformanceSummaryDisplayTitle(eventKey, data, base);
  }
  if (isRecordTradingDayEventKey(eventKey)) {
    return getRecordTradingDayDisplayTitle(eventKey, data, base);
  }
  if (isPnlMilestoneCrossedEventKey(eventKey)) {
    return getPnlMilestoneCrossedDisplayTitle(eventKey, data, base);
  }
  if (isBrokerDisconnectedEventKey(eventKey)) {
    return getBrokerConnectionDisplayTitle(eventKey, data, base);
  }
  if (isBrokerReconnectedEventKey(eventKey)) {
    return getBrokerConnectionDisplayTitle(eventKey, data, base);
  }
  if (isBrokerSyncEventKey(eventKey)) {
    return getBrokerConnectionDisplayTitle(eventKey, data, base);
  }
  return base;
}

/**
 * Friendly one- or two-line preview for dropdowns / lists (not the metric card layout).
 */
export function getPerformanceSummaryPreviewText(
  data: NotificationItem["data"]
): string {
  const obj = coerceDataRecord(data);
  const periodPhrase = obj ? getPerformanceSummaryPeriodPhrase(obj) : null;
  if (periodPhrase) {
    return `Your performance summary for ${periodPhrase} is ready. Click here to view.`;
  }
  return "Your performance summary is ready. Click here to view.";
}

/**
 * HTML metric tiles for `bodyMarkdown` paths (no React theme).
 * Visual tokens aligned with dashboard `RiskMetricsCard`: neutral surface, muted label, bold value.
 */
export function formatPerformanceSummaryAsCardHtml(data: NotificationItem["data"]): string {
  const entries = buildPerformanceSummaryEntries(data);
  if (!entries.length) return "";
  const cards = entries
    .map(
      (e) =>
        `<div style="border:1px solid var(--border);border-radius:0.5rem;padding:12px;background:var(--card);min-width:0;">
<div style="font-size:0.875rem;font-weight:500;color:var(--muted-foreground);text-transform:uppercase;opacity:0.9;line-height:1.2;margin-bottom:10px;font-family:'IBM Plex Sans',system-ui,sans-serif;">${escapeHtml(e.label)}</div>
<div style="font-size:1.25rem;font-weight:700;color:var(--foreground);line-height:1;font-family:'IBM Plex Sans',system-ui,sans-serif;word-break:break-word;">${escapeHtml(e.value)}</div>
</div>`
    )
    .join("");
  return `<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(148px,1fr));gap:12px;width:100%;">${cards}</div>`;
}

function coerceDataRecord(data: NotificationItem["data"]): Record<string, unknown> | null {
  if (data == null) return null;
  if (typeof data === "string") {
    const parsed = parseJsonObject(data);
    return parsed;
  }
  if (typeof data === "object" && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }
  return null;
}

function isSummaryStyleEvent(eventKey: string): boolean {
  return isPerformanceSummaryEventKey(eventKey);
}

function looksLikeTradeMetricsPayload(obj: Record<string, unknown>): boolean {
  const hints = [
    "total_trades",
    "closed_trades",
    "winning_trades",
    "losing_trades",
    "total_pnl",
    "net_pnl",
    "win_loss",
    "long_short",
    "by_symbol",
  ];
  return hints.some((k) => k in obj);
}

/**
 * HTML body for in-app notification previews (sidebar, modal, profile menu).
 * - If `body_markdown` is a JSON object string, render a readable list (escaped).
 * - For performance summary events, render `data` when the body is empty or still JSON-like.
 * - For new-best-day events, prefer supportive copy built from record fields.
 * - Otherwise return the original body (expected to be HTML from backend or rich text).
 */
export function getNotificationDisplayHtml(item: NotificationItem): string {
  const body = (item.bodyMarkdown ?? "").trim();
  const eventKey = item.eventKey ?? "";
  const data = coerceDataRecord(item.data);

  if (isNewBestTradingDayEventKey(eventKey)) {
    const bestDayHtml = formatNewBestTradingDayMessageHtml(item.data);
    if (bestDayHtml) return bestDayHtml;
  }

  if (isNewWorstTradingDayEventKey(eventKey)) {
    const worstDayHtml = formatNewWorstTradingDayMessageHtml(item.data);
    if (worstDayHtml) return worstDayHtml;
  }

  if (isPnlMilestoneCrossedEventKey(eventKey)) {
    const milestoneHtml = formatPnlMilestoneCrossedMessageHtml(item.data);
    if (milestoneHtml) return milestoneHtml;
  }

  if (isBrokerDisconnectedEventKey(eventKey)) {
    const disconnectedHtml = formatBrokerDisconnectedMessageHtml(item.data);
    if (disconnectedHtml) return disconnectedHtml;
  }

  if (isBrokerReconnectedEventKey(eventKey)) {
    const reconnectedHtml = formatBrokerReconnectedMessageHtml(item.data);
    if (reconnectedHtml) return reconnectedHtml;
  }

  if (isSyncFailedEventKey(eventKey)) {
    const syncFailedHtml = formatSyncFailedMessageHtml(item.data);
    if (syncFailedHtml) return syncFailedHtml;
  }

  if (isSyncRecoveredEventKey(eventKey)) {
    const syncRecoveredHtml = formatSyncRecoveredMessageHtml(item.data);
    if (syncRecoveredHtml) return syncRecoveredHtml;
  }

  if (isBillingPaymentSucceededEventKey(eventKey)) {
    const html = formatBillingPaymentSucceededMessageHtml(item.data);
    if (html) return html;
  }
  if (isBillingPaymentFailedEventKey(eventKey)) {
    const html = formatBillingPaymentFailedMessageHtml(item.data);
    if (html) return html;
  }
  if (isBillingPaymentActionRequiredEventKey(eventKey)) {
    const html = formatBillingPaymentActionRequiredMessageHtml(item.data);
    if (html) return html;
  }
  if (isBillingSubscriptionCancelledEventKey(eventKey)) {
    const html = formatBillingSubscriptionCancelledMessageHtml(item.data);
    if (html) return html;
  }
  if (isBillingSubscriptionUpdatedEventKey(eventKey)) {
    const html = formatBillingSubscriptionUpdatedMessageHtml(item.data);
    if (html) return html;
  }

  if (isReportReadyEventKey(eventKey)) {
    const html = formatReportReadyMessageHtml(item.data);
    if (html) return html;
  }

  if (isReportFailedEventKey(eventKey)) {
    const html = formatReportFailedMessageHtml(item.data);
    if (html) return html;
  }

  if (body && looksLikeJsonObject(body)) {
    const parsed = parseJsonObject(body);
    if (parsed && Object.keys(parsed).length > 0) {
      return formatPayloadAsHtml(parsed);
    }
  }

  if (isSummaryStyleEvent(eventKey) && data && Object.keys(data).length > 0) {
    return formatPerformanceSummaryAsCardHtml(data);
  }

  const bodyEmpty = !body || body === "{}";
  if (bodyEmpty && data && Object.keys(data).length > 0 && looksLikeTradeMetricsPayload(data)) {
    return formatPayloadAsHtml(data);
  }

  return item.bodyMarkdown ?? "";
}
